// src/components/ScrollToTop.js

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';

/**
 * Resets the window scroll to the top on every client-side navigation.
 *
 * BrowserRouter keeps the previous page's scroll offset when the route changes,
 * which made pages "load halfway" — e.g. opening the Refund Policy from a
 * scrolled-down product page, or pressing Back. Mounting this once inside
 * <Router> fixes it for every route in a single place.
 *
 * In-page section links are intentionally left alone: AppAppBar passes
 * { scrollTo } in location state and runs its own smooth scroll, and #hash
 * links should land on their anchor — so the reset is skipped in those cases.
 */
export default function ScrollToTop() {
  const { pathname, hash, state } = useLocation();

  // Take scroll handling away from the browser. Its async restore on
  // Back/Forward races with our lazy-loaded routes and is what made pages
  // come back "halfway"; we reset to the top ourselves instead.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Restore a captured scroll position (e.g. the Linen page's Back button
    // returning to where the visitor left off). Re-apply a few times as the
    // lazy-loaded route's content lays out and images settle.
    if (typeof state?.restoreY === 'number') {
      const target = state.restoreY;
      let attempts = 0;
      const apply = () => {
        window.scrollTo(0, target);
        attempts += 1;
        if (attempts < 6) setTimeout(apply, 70);
      };
      apply();
      return;
    }
    if (hash || state?.scrollTo) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, state]);

  // Fire GA4 page_view on every client-side route change. Deferred to next
  // tick so React Helmet has applied the new document.title before we read it.
  useEffect(() => {
    const id = setTimeout(() => {
      trackPageView({ path: pathname + (hash || ''), title: document.title });
    }, 0);
    return () => clearTimeout(id);
  }, [pathname, hash]);

  return null;
}
